// Shared language resolution used by both the i18next bootstrap (`i18n/index.ts`)
// and the theme config default (`config.ts`). Kept dependency-free so it can be
// imported from anywhere without risking a circular import.

import type { I18n } from 'types/config';

export const SUPPORTED_LOCALES: readonly I18n[] = ['en', 'uk'];
const FALLBACK_LOCALE: I18n = 'uk';

// Must match the key used by ConfigContext's useLocalStorage so we read back the
// user's previously persisted language choice.
const CONFIG_STORAGE_KEY = 'able-pro-material-react-ts-config';

function isSupported(value: string): value is I18n {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

// Resolution order: persisted user choice → browser locale → fallback.
export function resolveInitialLocale(): I18n {
  if (typeof window === 'undefined') return FALLBACK_LOCALE;

  try {
    const stored = localStorage.getItem(CONFIG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.i18n && isSupported(parsed.i18n)) return parsed.i18n;
    }
  } catch {
    // malformed config — fall through to browser detection
  }

  const browser = (navigator.language || '').split('-')[0].toLowerCase();
  if (isSupported(browser)) return browser;

  return FALLBACK_LOCALE;
}
